/**
 * Export Utilities
 */

/**
 * Convert measurement history to CSV format
 * @param {Array} history - Array of measurement objects
 * @returns {string} CSV string
 */
export const generateCSV = (history) => {
  if (!history || history.length === 0) return '';

  const headers = ['Date', 'Time', 'Heart Rate (bpm)', 'HRV RMSSD (ms)', 'Breathing Rate (rpm)', 'Signal Quality'];
  const rows = history.map(m => {
    const date = new Date(m.timestamp);
    return [
      date.toLocaleDateString(),
      date.toLocaleTimeString(),
      Math.round(m.heartRate),
      m.hrvRmssd,
      m.breathingRate,
      m.signalQuality
    ].join(',');
  });

  return [headers.join(','), ...rows].join('\n');
};

/**
 * Trigger a browser download
 * @param {string} content - File content
 * @param {string} filename - Name of the file
 * @param {string} type - MIME type (default text/csv)
 */
export const downloadFile = (content, filename, type = 'text/csv') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Open a printable report window
 * @param {Object} measurement - The measurement data to report
 */
export const printReport = (measurement) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to print the report.');
    return;
  }

  const date = new Date(measurement.timestamp || Date.now()).toLocaleString();

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Vital Scan Report - ${date}</title>
      <style>
        body { font-family: sans-serif; padding: 40px; color: #333; }
        h1 { color: #000; border-bottom: 2px solid #00ffcc; padding-bottom: 10px; }
        .header { margin-bottom: 30px; }
        .meta { color: #666; font-size: 0.9rem; }
        .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-bottom: 40px; }
        .card { border: 1px solid #ddd; padding: 20px; border-radius: 8px; text-align: center; }
        .label { display: block; font-size: 0.8rem; color: #666; text-transform: uppercase; margin-bottom: 5px; }
        .value { font-size: 2rem; font-weight: bold; color: #333; }
        .unit { font-size: 0.9rem; color: #999; }
        .footer { margin-top: 50px; font-size: 0.8rem; color: #999; text-align: center; border-top: 1px solid #eee; padding-top: 20px; }
        @media print {
          body { -webkit-print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Vital Scan Analysis Report</h1>
        <div class="meta">Generated on: ${date}</div>
      </div>

      <div class="grid">
        <div class="card">
          <span class="label">Heart Rate</span>
          <div class="value">${Math.round(measurement.heartRate)} <span class="unit">bpm</span></div>
        </div>
        <div class="card">
          <span class="label">HRV (RMSSD)</span>
          <div class="value">${measurement.hrvRmssd} <span class="unit">ms</span></div>
        </div>
        <div class="card">
          <span class="label">Breathing Rate</span>
          <div class="value">${measurement.breathingRate} <span class="unit">rpm</span></div>
        </div>
      </div>

      <div class="notes">
        <h3>Signal Quality: ${(measurement.signalQuality * 100).toFixed(0)}%</h3>
        <p>Using <strong>POS (Plane-Orthogonal-to-Skin)</strong> rPPG Algorithm.</p>
        <p><em>Note: This is a research prototype estimation, not a medical diagnosis.</em></p>
      </div>

      <div class="footer">
        Generated by Vital Scan AI Research Prototype
      </div>
      <script>
        window.onload = () => { window.print(); };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};
